import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@shared/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@shared/components/ui/form";
import { Input } from "@shared/components/ui/input";
import { Textarea } from "@shared/components/ui/textarea";
import { Card, CardHeader, CardContent } from "@shared/components/ui/card";
import { Badge } from "@shared/components/ui/badge";
import { useAuth } from "@features/auth/contexts/AuthContext";
import { MultiSelectProduct } from "@features/products/components/MultiSelectProduct";
import { useTranslation } from "react-i18next";
import { SelectCard } from "@/features/packages/components/PackageForm";
import {
  Package,
  Warehouse,
  Plus,
  User,
  Phone,
  MapPin,
  FileText,
  ShoppingCart,
} from "lucide-react";
import { usePackagesInfiniteQuery } from "@/features/packages/hooks/usePackages";
import { Fragment, useState } from "react";
import { PackageSelectCard } from "./PackageSelectCard";
import { SelectUser } from "@/features/users/SelectUser";

const formSchema = z
  .object({
    name: z.string().min(2, {
      message: "Name must be at least 2 characters.",
    }),
    pickupCity: z.string().min(2, {
      message: "Name must be at least 2 characters.",
    }),
    phone: z.string().min(2, {
      message: "Name must be at least 2 characters.",
    }),
    address: z.string().min(2, {
      message: "Name must be at least 2 characters.",
    }),
    type: z.string(),
    note: z.string(),
    products: z
      .array(
        z.object({
          _id: z.string(),
          name: z.string(),
          quantity: z.number().min(1),
          price: z.number().optional(),
          stock: z.number().optional(),
        })
      )
      .optional(),
    packages: z.array(z.string()).optional(),
  })
  .refine(
    (data) => {
      if (data.type === "product") {
        return data.products && data.products.length > 0;
      }
      if (data.type === "package") {
        return data.packages && data.packages.length > 0;
      }
      return true;
    },
    {
      message: "Please select at least one item",
      path: ["type"],
    }
  );

export function PickupForm({ onSubmit, isLoading: submitLoading }) {
  const auth = useAuth();
  const { data, isLoading } = usePackagesInfiniteQuery({
    status: "order_received",
    fromStock: false,
  });
  const { t } = useTranslation();
  const [selectedPackages, setSelectedPackages] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: auth.user.fullName,
      pickupCity: auth.user.city,
      phone: auth.user.phone,
      address: auth.user.city,
      products: [],
      type: "package",
      note: "",
      packages: [],
    },
  });

  const handleAddProducts = (products) => {
    setSelectedProducts(products);
    form.setValue("products", products);
  };

  const handleToggleCustomProduct = (type) => {
    form.setValue("type", type);
    if (type === "package") {
      form.setValue("products", []);
      setSelectedProducts([]);
      setSelectedPackages([]);
      form.setValue("packages", []);
    } else {
      form.setValue("packages", []);
      setSelectedPackages([]);
    }
  };

  const handlePackageSelect = (pkg) => {
    const isSelected = selectedPackages.some((p) => p._id === pkg._id);
    let newSelectedPackages;

    if (isSelected) {
      newSelectedPackages = selectedPackages.filter((p) => p._id !== pkg._id);
    } else {
      newSelectedPackages = [...selectedPackages, pkg];
    }

    setSelectedPackages(newSelectedPackages);
    // Update form value as well
    form.setValue(
      "packages",
      newSelectedPackages.map((p) => p._id)
    );
  };

  const handleSelectAllPackages = () => {
    const allPackages =
      data?.pages?.reduce((acc, page) => {
        return [...acc, ...page.data];
      }, []) || [];

    setSelectedPackages(allPackages);
    form.setValue(
      "packages",
      allPackages.map((p) => p._id)
    );
  };

  const handleDeselectAllPackages = () => {
    setSelectedPackages([]);
    form.setValue("packages", []);
  };

  const getAllPackages = () => {
    return (
      data?.pages?.reduce((acc, page) => {
        return [...acc, ...page.data];
      }, []) || []
    );
  };

  const areAllPackagesSelected = () => {
    const allPackages = getAllPackages();
    return (
      allPackages.length > 0 && selectedPackages.length === allPackages.length
    );
  };

  function handleSubmit(values) {
    const submitData = {
      ...values,
      // Ensure proper data structure
      products: values.type === "product" ? values.products || [] : [],
      packages: values.type === "package" ? values.packages || [] : [],
    };

    // If admin/manager is creating for a client, add the user field
    if ((auth.isAdmin || auth.isManager) && selectedClient) {
      submitData.user = selectedClient;
    }

    onSubmit(submitData);
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        {/* Client Selector for Admin/Manager */}
        {(auth.isAdmin || auth.isManager) && (
          <Card className="border-l-4 border-l-blue-500">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-2">
                <User className="h-5 w-5 text-blue-500" />
                <h3 className="text-lg font-semibold">
                  {t("users.selectClient")}
                </h3>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  {t("pickups.form.clientSelection")}
                </label>
                <SelectUser
                  onSelectUser={(userId) => setSelectedClient(userId)}
                  user={selectedClient}
                  role="client"
                  showDetails={true}
                />
                <p className="text-sm text-muted-foreground">
                  {t("pickups.form.clientSelectionHint")}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Customer Information Section */}
        <Card className="border-l-4 border-l-primary">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-2">
              <User className="h-5 w-5 text-primary" />
              <h3 className="text-lg font-semibold">
                {t("user.customerInfo")}
              </h3>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2 text-sm font-medium">
                      <User className="h-4 w-4" />
                      {t("user.fullName")}
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("user.fullName")}
                        {...field}
                        disabled
                        className="bg-muted/50 h-11"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2 text-sm font-medium">
                      <Phone className="h-4 w-4" />
                      {t("user.phone")}
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("user.phone")}
                        {...field}
                        disabled
                        className="bg-muted/50 h-11"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem className="md:col-span-2">
                    <FormLabel className="flex items-center gap-2 text-sm font-medium">
                      <MapPin className="h-4 w-4" />
                      {t("user.address")}
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("user.address")}
                        {...field}
                        disabled
                        className="bg-muted/50 h-11"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
        </Card>

        {/* Type Selection Section */}
        <Card className="border-l-4 border-l-blue-500">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5 text-blue-500" />
              <h3 className="text-lg font-semibold">
                {t("pickups.selectType")}
              </h3>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              <SelectCard
                icon={<Package className="h-5 w-5" />}
                title={t("packages.packages")}
                description={
                  isLoading
                    ? t("main.loading")
                    : `${data?.pages[0]?.totalItems || 0} ${t(
                        "main.available"
                      )}`
                }
                isSelected={form.getValues("type") === "package"}
                onSelect={() => handleToggleCustomProduct("package")}
              />
              <SelectCard
                icon={<Warehouse className="h-5 w-5" />}
                title={t("products.products")}
                isSelected={form.watch("type") === "product"}
                onSelect={() => handleToggleCustomProduct("product")}
              />
            </div>
          </CardContent>
        </Card>

        {/* Dynamic Content Section */}
        {form.getValues("type") === "product" ? (
          <Card className="border-l-4 border-l-green-500">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-2">
                <Warehouse className="h-5 w-5 text-green-500" />
                <h3 className="text-lg font-semibold">
                  {t("products.selectProducts")}
                </h3>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <MultiSelectProduct
                products={selectedProducts}
                onSelect={handleAddProducts}
                placeholder={t("pickups.selectProductsPlaceholder")}
                showQuantity={true}
                defaultQuantity={1}
                className="w-full"
              />

              {/* Validation message for product selection */}
              {form.formState.errors.type &&
                form.getValues("type") === "product" && (
                  <div className="mt-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
                    {form.formState.errors.type.message}
                  </div>
                )}
            </CardContent>
          </Card>
        ) : (
          <Card className="border-l-4 border-l-orange-500">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Package className="h-5 w-5 text-orange-500" />
                  <h3 className="text-lg font-semibold">
                    {t("packages.selectPackages")}
                  </h3>
                </div>
                <div className="flex items-center gap-2">
                  {!isLoading && getAllPackages().length > 0 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={
                        areAllPackagesSelected()
                          ? handleDeselectAllPackages
                          : handleSelectAllPackages
                      }
                      className="text-xs"
                    >
                      {areAllPackagesSelected()
                        ? t("common.deselectAll")
                        : t("common.selectAll")}
                    </Button>
                  )}
                  {selectedPackages.length > 0 && (
                    <Badge variant="secondary" className="ml-2">
                      {selectedPackages.length} {t("main.selected")}
                    </Badge>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                    <p className="mt-2 text-muted-foreground">
                      {t("main.loading")}
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {data?.pages?.map((page) => (
                      <Fragment key={page.pageInfo.currentPage}>
                        {page?.data.map((pkg) => (
                          <PackageSelectCard
                            key={pkg._id}
                            pkg={pkg}
                            isSelected={selectedPackages.some(
                              (p) => p._id === pkg._id
                            )}
                            onSelect={handlePackageSelect}
                          />
                        ))}
                      </Fragment>
                    ))}
                    {data?.pages?.[0]?.data?.length === 0 && (
                      <div className="text-center py-8 text-muted-foreground">
                        <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
                        <p>{t("packages.noPackagesAvailable")}</p>
                      </div>
                    )}
                  </div>

                  {/* Validation message for package selection */}
                  {form.formState.errors.type &&
                    form.getValues("type") === "package" && (
                      <div className="mt-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
                        {form.formState.errors.type.message}
                      </div>
                    )}
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Notes Section */}
        <Card className="border-l-4 border-l-purple-500">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-purple-500" />
              <h3 className="text-lg font-semibold">
                {t("common.additionalInfo")}
              </h3>
            </div>
          </CardHeader>
          <CardContent>
            <FormField
              control={form.control}
              name="note"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("common.note")}</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t("packages.form.notePlaceholder")}
                      {...field}
                      rows={4}
                      className="resize-none"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        {/* Summary Section */}
        <Card className="bg-muted/50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">
                  {t("pickups.summary")}
                </p>
                <div className="flex items-center gap-2">
                  {form.getValues("type") === "product" ? (
                    <>
                      <Warehouse className="h-4 w-4" />
                      <span className="font-medium">
                        {t("products.products")}
                      </span>
                      {selectedProducts.length > 0 && (
                        <>
                          <Badge variant="outline">
                            {selectedProducts.length} {t("products.product")}
                            {selectedProducts.length !== 1 ? "s" : ""}
                          </Badge>
                          <Badge variant="secondary">
                            {selectedProducts.reduce(
                              (total, product) =>
                                total + (product.quantity || 1),
                              0
                            )}{" "}
                            {t("main.total")} {t("main.items")}
                          </Badge>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <Package className="h-4 w-4" />
                      <span className="font-medium">
                        {t("packages.packages")}
                      </span>
                      <Badge variant="outline">
                        {selectedPackages.length} {t("main.selected")}
                      </Badge>
                    </>
                  )}
                </div>
              </div>
              <Button
                type="submit"
                size="lg"
                className="min-w-[120px]"
                disabled={
                  submitLoading ||
                  (form.getValues("type") === "product" &&
                    (!form.getValues("products") ||
                      form.getValues("products").length === 0)) ||
                  (form.getValues("type") === "package" &&
                    (!form.getValues("packages") ||
                      form.getValues("packages").length === 0))
                }
              >
                {submitLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {t("main.processing")}
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    {t("action.add", {
                      entity: t("pickups.pickup"),
                    })}
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </Form>
  );
}
